import { Form, Input } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../components/DraggableModal";
import { EntFarmOwner } from "../../../models/farmOwners";

enum FormField {
    pin,
    secondName,
    name,
    patronymic,
    contacts
}

interface IProps {
    show: boolean;
    farmOwner: EntFarmOwner;
    onHide: () => void;
    onSubmit: (pos: EntFarmOwner) => Promise<boolean>;
}

export const AddFarmOwnerModal = (props: IProps) => {
    const { t } = useTranslation();
    const [form] = useForm();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (props.show) setFieldsFromEntity();
    }, [props.show]);

    const setFieldsFromEntity = (): void => {
        form.setFields([
            { name: FormField.secondName, value: props.farmOwner.secondName },
            { name: FormField.patronymic, value: props.farmOwner.patronymic },
            { name: FormField.name, value: props.farmOwner.name },
            { name: FormField.pin, value: props.farmOwner.pin },
            { name: FormField.contacts, value: props.farmOwner.contacts },
        ]);
    }

    const setEntityFromFields = (): EntFarmOwner => {
        let farmOwner = _.cloneDeep(props.farmOwner);
        farmOwner.secondName = form.getFieldValue(FormField.secondName);
        farmOwner.patronymic = form.getFieldValue(FormField.patronymic);
        farmOwner.name = form.getFieldValue(FormField.name);
        farmOwner.pin = form.getFieldValue(FormField.pin);
        farmOwner.contacts = form.getFieldValue(FormField.contacts);
        return farmOwner;
    }

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await form.validateFields();
            const data = setEntityFromFields();
            const result = await props.onSubmit(data);
            if (result) props.onHide();
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <DraggableModal
                title={props.farmOwner.id === 0 ? t('registers.register_of_farmowners.add_owner') : t('registers.register_of_farmowners.edit_owner') }
                visible={props.show}
                onCancel={props.onHide}
                onSubmit={handleSubmit}
                submitLoading={loading}
            >
                <Form
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                    form={form}
                >

                    <Form.Item
                        label={ t('user.name_employee') }
                        name={FormField.secondName}
                        rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                        label={ t('user.surname') }
                        name={FormField.name}
                        rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                        label={ t('user.last_name') }
                        name={FormField.patronymic}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                        label={ t('user.pin') }
                        name={FormField.pin}
                        rules={[
                            { required: true, message: t("msg.fill_in_the_field") },
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.resolve(); 
                                    }

                                    const pinPattern = /^(1|2)(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(19\d{2}|20[0-2]\d)\d{5}$/;
                                    if (!pinPattern.test(value)) {
                                        return Promise.reject(new Error(t('msg.error_fill_pin')));
                                    }

                                    return Promise.resolve();
                                },
                            },
                            {
                                len: 14,
                                message: t('msg.desc_fill_pin'),
                            },
                        ]}
                    >
                        <Input minLength={14} maxLength={14} showCount autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                        label={ t('user.contacts') }
                        name={FormField.contacts}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>
                </Form>
            </DraggableModal>
        </>
    )
}
